// src/components/Header.js - VERSION CORRIGÉE
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Modifiez la déclaration de PROFILE_OPTIONS :
const PROFILE_OPTIONS = (() => {
  try {
    const parsed = JSON.parse(process.env.REACT_APP_PROFILE_OPTIONS || "[]");
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    console.error("Erreur parsing PROFILE_OPTIONS:", error);
  }
  // Fallback si le parsing échoue
  return [
    { value: "1", label: "Administrateur" },
    { value: "2", label: "Contrôleur" },
    { value: "3", label: "Gestionnaire" },
    { value: "4", label: "Agent" },
    { value: "5", label: "Poste de contrôle" }
  ];
})();

const Header = () => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [societeLogo, setSocieteLogo] = useState(null);
  const [societeNom, setSocieteNom] = useState(null);
  const navigate = useNavigate();

  // Charger les infos utilisateur
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Erreur parsing user :", e);
    }
  }, []);

  // Logo de la société connectée : remplace le logo SecureControl dans l'en-tête
  // pour tout le monde sauf l'admin (qui supervise plusieurs sociétés, sans
  // logo unique pertinent à afficher).
  useEffect(() => {
    if (!user || Number(user.profile) === 1) return;
    const token = localStorage.getItem("access_token");
    if (!token) return;

    axios.get(`${API_BASE_URL}/societes/my_logo.php`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data?.logo_base64) {
        setSocieteLogo(res.data.logo_base64);
      }
      if (res.data?.nom_societe) {
        setSocieteNom(res.data.nom_societe);
      }
    }).catch(() => {});
  }, [user]);

  // Écouter les mises à jour de logo société depuis SocieteEdit (navigation SPA :
  // Header ne se remonte pas, donc pas de refetch automatique sans cet événement).
  useEffect(() => {
    const handleLogoUpdate = (event) => {
      const { logoBase64 } = event.detail || {};
      if (logoBase64) setSocieteLogo(logoBase64);
    };
    window.addEventListener('societeLogoUpdated', handleLogoUpdate);
    return () => window.removeEventListener('societeLogoUpdated', handleLogoUpdate);
  }, []);

  // Charger les notifications (seulement pour admin)
  const loadNotifications = useCallback(async () => {
    if (!user || Number(user.profile) !== 1) return;

    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await axios.get(
        `${API_BASE_URL}/auth/notifications.php`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const notifs = response.data.data.notifications || [];
        const count = response.data.data.unread_count || notifs.length;
        setNotifications(notifs);
        setUnreadCount(count);
      }
    } catch (error) {
      console.error("Erreur notifications:", error);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Écouter les mises à jour d'avatar depuis ProfilePage
  useEffect(() => {
    const handleAvatarUpdate = (event) => {
      const { avatarUrl } = event.detail;
      if (avatarUrl && user) {
        try {
          const stored = localStorage.getItem("user");
          if (stored) {
            const updatedUser = JSON.parse(stored);
            // Stocker dans les 3 propriétés pour compatibilité
            updatedUser.avatar = avatarUrl;
            updatedUser.avatar_url = avatarUrl;
            updatedUser.avatarUrl = avatarUrl;
            localStorage.setItem("user", JSON.stringify(updatedUser));
            setUser(updatedUser);
          }
        } catch (e) {
          console.error("Erreur mise à jour localStorage:", e);
        }
      }
    };

    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    
    return () => {
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
    };
  }, [user]);

  // Fonctions pour le profil
  const getDisplayName = () => {
    if (!user) return "Compte";
    const fullName = `${user.prenom || ""} ${user.nom || ""}`.trim();
    return fullName || user.username || "Compte";
  };

  const profileLabel = (profileValue) => {
    if (profileValue === null || profileValue === undefined) return "";
    
    // Convertir en chaîne pour la comparaison
    const str = String(profileValue).trim();
    if (!str) return "";

    // Chercher avec conversion de type
    let found = PROFILE_OPTIONS.find((opt) => {
      const optValue = String(opt.value).trim();
      return optValue === str || opt.value == str;
    });
    
    // Si toujours pas trouvé, retourner la valeur d'origine
    return found ? found.label : `Profil ${profileValue}`;
  };

  const getSubtitle = () => {
    if (!user) return "";
    return profileLabel(user.poste || user.profile || "");
  };

  // FONCTION CORRIGÉE pour obtenir l'URL de l'avatar
  const getAvatarSrc = () => {
    if (!user) return "/assets/img/utilisateur.png";
    
    // 1. Priorité: avatar (champ retourné par l'API login)
    if (user.avatar && user.avatar.trim() !== '') {
      // Si c'est déjà une URL complète
      if (user.avatar.startsWith('http')) {
        return user.avatar;
      }
      // Si c'est un chemin relatif
      const base = (API_BASE_URL || "http://localhost/securecontrol-backend")
        .replace(/\/+$/, "");
      const cleanPath = user.avatar.replace(/^\/+/, "");
      return `${base}/${cleanPath}`;
    }
    
    // 2. Fallback: avatar_url
    if (user.avatar_url && user.avatar_url.trim() !== '') {
      if (user.avatar_url.startsWith('http')) {
        return user.avatar_url;
      }
      const base = (API_BASE_URL || "http://localhost/securecontrol-backend")
        .replace(/\/+$/, "");
      const cleanPath = user.avatar_url.replace(/^\/+/, "");
      return `${base}/${cleanPath}`;
    }
    
    // 3. Fallback: avatar_path
    if (user.avatar_path && user.avatar_path.trim() !== '') {
      const base = (API_BASE_URL || "http://localhost/securecontrol-backend")
        .replace(/\/+$/, "");
      const cleanPath = user.avatar_path.replace(/^\/+/, "");
      return `${base}/${cleanPath}`;
    }
    
    // 4. Fallback: avatarUrl (ancien format)
    if (user.avatarUrl && user.avatarUrl.trim() !== '') {
      if (user.avatarUrl.startsWith('http')) {
        return user.avatarUrl;
      }
      const base = (API_BASE_URL || "http://localhost/securecontrol-backend")
        .replace(/\/+$/, "");
      const cleanPath = user.avatarUrl.replace(/^\/+/, "");
      return `${base}/${cleanPath}`;
    }
    
    // 5. Image par défaut
    return "/assets/img/utilisateur.png";
  };

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    // Navigation complète (comme l'intercepteur 401/403 dans index.js), pas un
    // simple push react-router : sans rechargement complet, un retour arrière
    // navigateur pouvait réafficher la page authentifiée depuis le bfcache
    // avant qu'un nouveau rendu ne détecte l'absence de token.
    window.location.href = "/login";
  };

  // FONCTION POUR BASCULER LE SIDEBAR
  const toggleSidebar = () => {
    const body = document.querySelector("body");
    if (body.classList.contains("toggle-sidebar")) {
      body.classList.remove("toggle-sidebar");
    } else {
      body.classList.add("toggle-sidebar");
    }
  };

  // Vérifier si admin
  const isAdmin = user && Number(user.profile) === 1;

  return (
    <header id="header" className="header fixed-top d-flex align-items-center">
      <div className="d-flex align-items-center justify-content-between">
        <Link to="/dashboard" className="logo d-flex align-items-center">
          <div className="logo-container position-relative">
            <img
              src={societeLogo || "/assets/img/logosecurelarge.png"}
              alt="Logo"
              className="logo-img"
              style={{ maxHeight: "40px", maxWidth: "160px", width: "auto", objectFit: "contain" }}
              onError={(e) => { e.target.onerror = null; e.target.src = "/assets/img/logosecurelarge.png"; }}
            />
          </div>
          {societeNom && (
            <span className="d-none d-md-inline ms-2 fw-bold" style={{ color: "#0B3C5D", fontSize: "1rem" }}>
              {societeNom}
            </span>
          )}
        </Link>
        {/* BOUTON TOGGLE AVEC GESTIONNAIRE DE CLIC */}
        <i
          className="bi bi-list toggle-sidebar-btn"
          onClick={toggleSidebar}
          style={{ cursor: "pointer" }}
        ></i>
      </div>

      <nav className="header-nav ms-auto">
        <ul className="d-flex align-items-center">
          {/* NOTIFICATIONS */}
          {isAdmin && (
            <li className="nav-item dropdown">
              <a
                className="nav-link nav-icon position-relative"
                href="#"
                data-bs-toggle="dropdown"
                style={{ fontSize: '1.2rem' }}
              >
                <i className="bi bi-bell"></i>
                {unreadCount > 0 && (
                  <span className="badge bg-danger badge-number" style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    fontSize: '0.6rem',
                    padding: '2px 5px',
                    borderRadius: '50%',
                    minWidth: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </a>
              
              {/* Menu des notifications */}
              <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow">
                <li className="dropdown-header">
                  <h6 className="mb-0">Notifications</h6>
                  {unreadCount > 0 && (
                    <span className="badge bg-danger ms-2">{unreadCount}</span>
                  )}
                </li>
                <li><hr className="dropdown-divider" /></li>
                
                {notifications.length > 0 ? (
                  notifications.slice(0, 3).map((notif, index) => (
                    <li key={notif.id || index}>
                      <a 
                        className="dropdown-item d-flex align-items-center" 
                        href={notif.link || "#"}
                        onClick={(e) => {
                          if (notif.link) {
                            e.preventDefault();
                            navigate(notif.link);
                          }
                        }}
                      >
                        <i className={`bi bi-${notif.icon || 'bell'} me-2 text-${notif.type || 'primary'}`}></i>
                        <div>
                          <div className="fw-semibold">{notif.title}</div>
                          <small className="text-muted">{notif.message}</small>
                        </div>
                      </a>
                    </li>
                  ))
                ) : (
                  <li className="dropdown-item text-muted text-center">
                    Aucune notification
                  </li>
                )}
                
                {notifications.length > 3 && (
                  <>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <Link 
                        className="dropdown-item text-center" 
                        to="/notifications"
                      >
                        Voir toutes ({notifications.length})
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </li>
          )}

          {/* PROFILE */}
          <li className="nav-item dropdown pe-3">
            <a
              className="nav-link nav-profile d-flex align-items-center pe-0"
              href="#"
              data-bs-toggle="dropdown"
            >
              <img
                src={getAvatarSrc()}
                alt="Profile"
                className="rounded-circle"
                style={{ width: 32, height: 32, objectFit: "cover" }}
                onError={(e) => {
                  // Fallback en cas d'erreur de chargement
                  e.target.src = "/assets/img/utilisateur.png";
                }}
              />
              <span className="d-none d-md-block dropdown-toggle ps-2">
                {getDisplayName()}
              </span>
            </a>

            <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile">
              <li className="dropdown-header">
                <h6>{getDisplayName()}</h6>
                <span>{getSubtitle()}</span>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>

              <li>
                <Link
                  className="dropdown-item d-flex align-items-center"
                  to="/profile"
                >
                  <i className="bi bi-person"></i>
                  <span>Mon profil</span>
                </Link>
              </li>

              <li>
                <hr className="dropdown-divider" />
              </li>

              <li>
                <a
                  href="#"
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right"></i>
                  <span>Déconnexion</span>
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;